import { useDispatch, useSelector } from 'react-redux';
import { useFrontendSubscriptionStatus } from '@cmctechnology/webinvest-store-frontend';
import { INSTRUMENT_STORE_NAME, InstrumentStore } from './store';
import { useEffect } from 'react';
import { INSTRUMENT_API_NAMES, fetchInstruments, fetchDividends, fetchNews, fetchTiprankSupport } from './actions';
import { fetchEsg } from './esgActions';

type IStore = ReturnType<typeof InstrumentStore.getState>;

/**
 * Pass instrument code, if not yet fetched, it will be requested.
 * @param {string} instrument - instrument code
 * @param fullDetails - request details if `fullDetail` is not yet fetched
 */
export const useInstrument = (instrument: string, fullDetails?: boolean, fetchLogo: boolean = true) => {
  const dispatch = useDispatch();
  const details = useSelector((store: IStore) => store.instrument.details[instrument]);
  const logo = useSelector((store: IStore) => store.instrument.logos[instrument]);
  const { error, fetching } = useFrontendSubscriptionStatus(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.ProductDetailsGet3);

  useEffect(() => {
    dispatch<any>(fetchInstruments(instrument, fullDetails, fetchLogo));
  }, [instrument]);

  return {
    // Note: frontend.apiCall share the same frontend status in instrument key
    // Assume current fetch is for current code if `details` is not available
    loading: !details && fetching,
    error: !details && error,
    data: {
      details,
      logo,
      code: instrument
    }
  };
};

/**
 * Pass instrument codes, if not yet fetched, they will be requested.
 * @param {string[]} instruments - array of instrument codes
 * @param fullDetails - request details if `fullDetail` is not yet fetched
 */
export const useInstruments = (instruments: string[], fullDetails?: boolean, fetchLogo: boolean = true) => {
  const dispatch = useDispatch();
  const detailsMap = useSelector((store: IStore) => store.instrument.details);
  const logosMap = useSelector((store: IStore) => store.instrument.logos);
  const { error, fetching } = useFrontendSubscriptionStatus(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.ProductDetailsGet3);

  useEffect(() => {
    dispatch<any>(fetchInstruments(instruments, fullDetails, fetchLogo));
  }, [JSON.stringify(instruments)]);

  const instrumentDetails = instruments.map((code) => ({
    details: detailsMap[code],
    logo: logosMap[code],
    code
  }));

  return {
    loading: instrumentDetails.some((item) => !item.details) && fetching,
    error: instrumentDetails.some((item) => !item.details) && error,
    data: instrumentDetails
  };
};

/**
 * Pass instrument code, if not yet fetched, it will be requested.
 * @param {string} instrument - instrument code
 */
export const useDividends = (instrument: string) => {
  const dispatch = useDispatch();
  const details = useSelector((store: IStore) => store.instrument.dividends[instrument]);
  const { error, fetching } = useFrontendSubscriptionStatus(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.UpcomingDividendsGet1);

  useEffect(() => {
    dispatch<any>(fetchDividends(instrument));
  }, [instrument]);

  return {
    // Note: frontend.apiCall share the same frontend status in instrument key
    // Assume current fetch is for current code if `details` is not available
    loading: !details && fetching,
    error,
    data: details
  };
};

/**
 * Pass instrument code, if not yet fetched, it will be requested.
 * @param {string} instrument - instrument code
 */
export const useNews = (instrument: string) => {
  const dispatch = useDispatch();
  const details = useSelector((store: IStore) => store.instrument.news[instrument]);
  const { error, fetching } = useFrontendSubscriptionStatus(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.NewsIndicatorBatchStockGet1);

  useEffect(() => {
    dispatch<any>(fetchNews(instrument));
  }, [instrument]);

  return {
    // Note: frontend.apiCall share the same frontend status in instrument key
    // Assume current fetch is for current code if `details` is not available
    loading: !details && fetching,
    error,
    data: details
  };
};

export const useTiprankSupport = (instrument: string) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch<any>(fetchTiprankSupport(instrument));
  }, [instrument]);

  return useSelector((store: IStore) => store.instrument.tipRanks[instrument]);
};

/**
 * Pass instrument code, if not yet fetched, it will be requested.
 * @param {string} instrument - instrument code
 */
export const useEsg = (instrument: string) => {
  const dispatch = useDispatch();
  const details = useSelector((store: IStore) => store.instrument.esg[instrument]);
  const { error, fetching } = useFrontendSubscriptionStatus(INSTRUMENT_STORE_NAME, INSTRUMENT_API_NAMES.ResearchESGDataGet1, instrument);

  useEffect(() => {
    dispatch<any>(fetchEsg(instrument));
  }, [instrument]);

  return {
    // Note: frontend.apiCall share the same frontend status in instrument key
    // Assume current fetch is for current code if `details` is not available
    loading: !details && fetching,
    error,
    data: details
  };
};
